/**
 * Footer
 * ======
 * Dark, editorial footer — a hairline border rather than a colour block
 * to separate it from the page, with the same restrained type system.
 * Hover states are colour-only and stationary throughout.
 */

import { Link } from 'react-router-dom';
import {
  BUSINESS_INFO,
  SOCIAL_LINKS,
  OPENING_HOURS_DISPLAY,
  NAV_LINKS,
  EXTERNAL_URLS
} from '../config/constants';
import { formatPhoneForLink, createMailtoLink } from '../utils/seoHelpers';
import { isUKBankHoliday } from '../utils/bankHolidays';

const SOCIALS = [
  {
    name: 'Instagram',
    href: SOCIAL_LINKS.instagram,
    path: 'M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z'
  },
  {
    name: 'Facebook',
    href: SOCIAL_LINKS.facebook,
    path: 'M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z'
  },
  {
    name: 'TikTok',
    href: SOCIAL_LINKS.tiktok,
    path: 'M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z'
  },
  {
    name: 'WhatsApp Channel',
    href: SOCIAL_LINKS.whatsapp,
    path: 'M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884M20.52 3.449C18.24 1.245 15.24 0 12.045 0 5.463 0 .104 5.358.101 11.94c0 2.096.549 4.14 1.595 5.945L0 24l6.335-1.652a11.882 11.882 0 005.71 1.447h.005c6.585 0 11.946-5.359 11.949-11.943 0-3.189-1.24-6.188-3.495-8.443'
  }
];

const CONTACT_ROWS = [
  {
    href: EXTERNAL_URLS.googleMapsDirections,
    value: BUSINESS_INFO.address.full,
    icon: <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0zM15 11a3 3 0 11-6 0 3 3 0 016 0z" />
  },
  {
    href: `tel:${formatPhoneForLink(BUSINESS_INFO.phone)}`,
    value: BUSINESS_INFO.phone,
    icon: <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
  },
  {
    href: createMailtoLink(BUSINESS_INFO.email),
    value: BUSINESS_INFO.email,
    icon: <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />,
    breakAll: true
  }
];

const ACTION_LINKS = [
  { label: 'Order for collection', href: EXTERNAL_URLS.ordering },
  { label: 'Order delivery', to: '/order-online#delivery' },
  { label: 'Book a table', href: EXTERNAL_URLS.reservation },
  { label: 'Join rewards', href: EXTERNAL_URLS.loyalty }
];

const ActionLink = ({ action }) => {
  const content = (
    <span className="inline-flex items-center gap-2 text-amber-bright transition-colors duration-300 ease-out hover:text-amber-400">
      {action.label}
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
      </svg>
    </span>
  );
  return action.to ? (
    <Link to={action.to} className="w-fit">{content}</Link>
  ) : (
    <a href={action.href} target="_blank" rel="noopener noreferrer" className="w-fit">{content}</a>
  );
};

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const isBankHolidayToday = isUKBankHoliday(new Date());

  return (
    <footer className="bg-surface border-t border-line text-cream" role="contentinfo">
      <div className="container-custom py-20 lg:py-28">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-10 lg:gap-x-12 gap-y-14 md:gap-y-16">
          {/* Brand */}
          <div>
            <Link to="/" className="inline-flex items-center gap-3 mb-7" aria-label="Gup Shup Home">
              <img src="/images/branding/logo.svg" alt="" aria-hidden="true" className="h-20 w-auto flex-shrink-0" />
              <span className="flex flex-col leading-none">
                <span className="font-display text-2xl text-cream">Gup Shup</span>
                <span className="font-sans text-2xs tracking-widest2 uppercase text-cream-faint mt-1.5">Chit Chat Chai</span>
              </span>
            </Link>
            <p className="text-cream-dim text-sm leading-relaxed font-sans mb-7 max-w-[26ch]">
              {BUSINESS_INFO.tagline}. Bringing the flavours of India and Pakistan to Collier Row, Romford.
            </p>
            <div className="flex gap-3">
              {SOCIALS.map((s) => (
                <a
                  key={s.name}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 flex items-center justify-center border border-line text-cream-dim transition-colors duration-300 ease-out hover:text-amber-bright hover:border-amber-bright/60"
                  aria-label={`Follow us on ${s.name}`}
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d={s.path} /></svg>
                </a>
              ))}
            </div>
            <p className="mt-4 font-sans text-xs text-cream-faint leading-relaxed max-w-[30ch]">
              Follow our WhatsApp Channel for menu updates, specials &amp; offers.
            </p>
          </div>

          {/* Explore */}
          <div>
            <h3 className="eyebrow mb-7">Explore</h3>
            <nav aria-label="Footer navigation">
              <ul className="space-y-4">
                {NAV_LINKS.map((link) => (
                  <li key={link.path}>
                    <Link to={link.path} className="font-sans text-sm text-cream-dim transition-colors duration-300 ease-out hover:text-amber-bright">
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          </div>

          {/* Hours */}
          <div>
            <h3 className="eyebrow mb-7">Opening Hours</h3>
            {isBankHolidayToday && (
              <p className="text-amber-bright font-sans text-xs font-semibold mb-4">
                Bank Holiday today — open 9:00am – 10:00pm
              </p>
            )}
            <ul className="space-y-3.5 font-sans text-sm leading-relaxed">
              {OPENING_HOURS_DISPLAY.map((schedule) => {
                const isHighlighted = isBankHolidayToday && schedule.day === 'Bank Holidays';
                const isClosed = schedule.hours === 'Closed';
                return (
                  <li key={schedule.day} className="flex items-baseline justify-between gap-4">
                    <span className={isHighlighted ? 'text-amber-bright font-semibold' : 'text-cream-dim'}>
                      {schedule.day}
                    </span>
                    <span className={`tabular-nums text-right ${
                      isHighlighted ? 'text-amber-bright font-semibold' : isClosed ? 'text-cream-faint' : 'text-cream font-medium'
                    }`}>
                      {schedule.hours}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Contact + CTAs */}
          <div>
            <h3 className="eyebrow mb-7">Visit / Contact</h3>
            <address className="not-italic space-y-4 font-sans text-sm mb-8">
              {CONTACT_ROWS.map((row) => (
                <a
                  key={row.value}
                  href={row.href}
                  target={row.href.startsWith('http') ? '_blank' : undefined}
                  rel={row.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                  className="flex items-start gap-3 text-cream-dim transition-colors duration-300 ease-out hover:text-amber-bright"
                >
                  <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75} aria-hidden="true">
                    {row.icon}
                  </svg>
                  <span className={row.breakAll ? 'break-all' : ''}>{row.value}</span>
                </a>
              ))}
            </address>

            <div className="pt-6 border-t border-line/60 flex flex-col gap-3.5 font-sans text-sm">
              {ACTION_LINKS.map((action) => (
                <ActionLink key={action.label} action={action} />
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-line">
        <div className="container-custom py-7 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 font-sans text-xs text-cream-faint">
          <p>&copy; {currentYear} {BUSINESS_INFO.name}. All rights reserved.</p>
          <div className="flex gap-8">
            <Link to="/privacy-policy" className="transition-colors duration-300 ease-out hover:text-cream">Privacy Policy</Link>
            <Link to="/terms" className="transition-colors duration-300 ease-out hover:text-cream">Terms of Service</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
